import React, { useState, useEffect } from 'react';

const GroupAvailability = ({ groupId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [members, setMembers] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [loading, setLoading] = useState(true);

  // Mock data - replace with actual API calls
  const mockMembers = [
    { id: 1, username: 'john_doe', color: '#4CBED9' },
    { id: 2, username: 'jane_smith', color: '#10B981' },
    { id: 3, username: 'mike_wilson', color: '#F59E0B' },
    { id: 4, username: 'alice_brown', color: '#EF4444' }
  ];

  const mockAvailability = {
    '2025-01-12-09:00': [1, 2],
    '2025-01-12-10:00': [1, 2, 3],
    '2025-01-12-14:00': [1, 3, 4],
    '2025-01-12-15:00': [2, 3, 4],
    '2025-01-13-09:00': [1, 4],
    '2025-01-13-10:00': [1, 2, 4],
    '2025-01-13-19:00': [1, 2, 3, 4],
    '2025-01-14-14:00': [2, 3],
    '2025-01-14-15:00': [1, 2, 3],
    '2025-01-15-09:00': [1, 3, 4],
    '2025-01-15-18:00': [1, 2, 3, 4]
  };

  useEffect(() => {
    fetchGroupMembers();
    fetchAvailability();
  }, [groupId, currentDate]);

  const fetchGroupMembers = async () => {
    try {
      // Replace with actual API call
      // const response = await api.get(`/api/groups/${groupId}/members`);
      // setMembers(response.data);
      setMembers(mockMembers);
    } catch (error) {    }
  };

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      // const response = await api.get(`/api/groups/${groupId}/availability`, {
      //   params: { date: currentDate.toISOString() }
      // });
      // setAvailability(response.data);
      setAvailability(mockAvailability);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const updateAvailability = async (dateTimeSlot, isAvailable) => {
    try {
      // Replace with actual API call
      // await api.post(`/api/groups/${groupId}/availability`, {
      //   slot: dateTimeSlot,
      //   available: isAvailable
      // });

      // Update locally
      setAvailability(prev => ({
        ...prev,
        [dateTimeSlot]: isAvailable 
          ? [...(prev[dateTimeSlot] || []), 1] // Assuming current user ID is 1
          : (prev[dateTimeSlot] || []).filter(id => id !== 1)
      }));
    } catch (error) {    }
  };

  const toggleSlotSelection = (dateTimeSlot) => {
    setSelectedSlots(prev => 
      prev.includes(dateTimeSlot)
        ? prev.filter(slot => slot !== dateTimeSlot)
        : [...prev, dateTimeSlot]
    );
  };

  const getWeekDays = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  const getDateTimeKey = (date, time) => {
    return `${date.toISOString().split('T')[0]}-${time}`;
  };

  const getAvailabilityCount = (dateTimeKey) => {
    return availability[dateTimeKey]?.length || 0;
  };

  const isCurrentUserAvailable = (dateTimeKey) => {
    return availability[dateTimeKey]?.includes(1) || false; // Assuming current user ID is 1
  };

  const getAvailabilityColor = (count, total) => {
    const percentage = count / total;
    if (percentage >= 0.75) return 'bg-green-500';
    if (percentage >= 0.5) return 'bg-yellow-500';
    if (percentage >= 0.25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const createEventFromSlots = async () => {
    if (selectedSlots.length === 0) return;

    try {
      // Find the most popular time slot
      const slotCounts = selectedSlots.map(slot => ({
        slot,
        count: getAvailabilityCount(slot)
      }));
      
      const bestSlot = slotCounts.reduce((best, current) => 
        current.count > best.count ? current : best
      );

      // Navigate to create event with suggested time
      const [dateStr, timeStr] = bestSlot.slot.split('-');
      const eventDate = new Date(dateStr + 'T' + timeStr);
      
      // This would typically navigate to create event page with pre-filled data      // Clear selections
      setSelectedSlots([]);
    } catch (error) {    }
  };

  const weekDays = getWeekDays(currentDate);
  const timeSlots = getTimeSlots();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Group Availability</h2>
        <div className="flex items-center space-x-4">
          {selectedSlots.length > 0 && (
            <button
              onClick={createEventFromSlots}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Create Event ({selectedSlots.length} slots)
            </button>
          )}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-lg font-medium">
              {weekDays[0].toLocaleDateString()} - {weekDays[6].toLocaleDateString()}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6">
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span>High availability (75%+)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
            <span>Good availability (50-75%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
            <span>Low availability (25-50%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
            <span>Very low availability (0-25%)</span>
          </div>
        </div>
        
        <div className="mt-2 flex items-center space-x-4 text-sm">
          <span className="font-medium">Members ({members.length}):</span>
          {members.map(member => (
            <div key={member.id} className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-1" 
                style={{ backgroundColor: member.color }}
              ></div>
              <span>{member.username}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header */}
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div className="p-2 text-center font-medium text-gray-600">Time</div>
            {weekDays.map(day => (
              <div key={day.toISOString()} className="p-2 text-center font-medium text-gray-900">
                <div className="text-sm">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className="text-lg">{day.getDate()}</div>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          {timeSlots.map(time => (
            <div key={time} className="grid grid-cols-8 gap-1 mb-1">
              <div className="p-2 text-center text-sm text-gray-600 font-medium">
                {time}
              </div>
              {weekDays.map(day => {
                const dateTimeKey = getDateTimeKey(day, time);
                const availabilityCount = getAvailabilityCount(dateTimeKey);
                const isUserAvailable = isCurrentUserAvailable(dateTimeKey);
                const isSelected = selectedSlots.includes(dateTimeKey);
                
                return (
                  <div key={dateTimeKey} className="relative">
                    <button
                      onClick={() => {
                        updateAvailability(dateTimeKey, !isUserAvailable);
                        if (availabilityCount > 0) {
                          toggleSlotSelection(dateTimeKey);
                        }
                      }}
                      className={`w-full h-12 rounded border-2 transition-all ${
                        isSelected 
                          ? 'border-primary-600 ring-2 ring-primary-300' 
                          : 'border-gray-200 hover:border-gray-300'
                      } ${
                        availabilityCount > 0 
                          ? getAvailabilityColor(availabilityCount, members.length)
                          : 'bg-gray-100'
                      } ${isUserAvailable ? 'ring-2 ring-cyan-400' : ''}`}
                      title={`${availabilityCount}/${members.length} available${isUserAvailable ? ' (including you)' : ''}`}
                    >
                      <div className="text-xs text-white font-bold">
                        {availabilityCount > 0 && `${availabilityCount}/${members.length}`}
                      </div>
                    </button>
                    
                    {/* Show member indicators */}
                    {availability[dateTimeKey] && availability[dateTimeKey].length > 0 && (
                      <div className="absolute bottom-1 left-1 flex space-x-1">
                        {availability[dateTimeKey].slice(0, 3).map((memberId, index) => {
                          const member = members.find(m => m.id === memberId);
                          return member ? (
                            <div
                              key={memberId}
                              className="w-2 h-2 rounded-full border border-white"
                              style={{ backgroundColor: member.color }}
                            />
                          ) : null;
                        })}
                        {availability[dateTimeKey].length > 3 && (
                          <div className="w-2 h-2 rounded-full bg-gray-600 border border-white"></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-cyan-50 rounded-lg">
        <h3 className="font-medium text-cyan-900 mb-2">How to use:</h3>
        <ul className="text-sm text-cyan-800 space-y-1">
          <li>• Click on time slots to toggle your availability</li>
          <li>• Your available slots will have a cyan ring</li>
          <li>• Click on slots with good availability to select them for event creation</li>
          <li>• Use "Create Event" button to create an event for selected time slots</li>
        </ul>
      </div>
    </div>
  );
};

export default GroupAvailability;